import React from 'react';
import filter from 'lodash/filter';
import sortBy from 'lodash/sortBy';
import some from 'lodash/some';

import { Box, Chip, Typography, Avatar, Divider } from '@mui/material';
import Swal from 'sweetalert2';
import { useTheme } from '@mui/system';
import { useGetCategories } from '@hooks/useGetCategories';
import { useGetPopularBrands } from '@hooks/useGetPopularBrands';
import { useGetUserData } from '@hooks/useGetUserData';
import { getGetPageDeals } from '@hooks/useGetPageDeal';
import { useGetDealListsEnabled } from '@hooks/useDealLists';

import {
  // bannedBrands,
  bannedCategories
} from '@components/AdminSlugActions/utils';
import { handleOpenModalPropsOptions } from '@components/AdminSlugActions/components/EditPageDeal/usePageDealState';
import Loading from '@components/Loading';
import { PageDealType, PopularBrand, DealList, CategoryType } from '@types';
import { useGetBannedTerms, useAddBannedTerm } from '@hooks/useBannedTerms';

interface AvailableNewPageDealsProps {
  handleOpenModal: (
    pageDeal: PageDealType | null,
    props: handleOpenModalPropsOptions
  ) => void;
}

interface MenuItemType {
  name?: string;
  brand?: string;
  second_category?: string;
  category?: string;
  numLiveDeals?: number;
  count?: number;
  [key: string]: any;
}

const AvailableNewPageDeals = ({
  handleOpenModal
}: AvailableNewPageDealsProps) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { mutate: addBannedTerm } = useAddBannedTerm();
  const { data: pageDeals, isLoading: isLoadingPageDeals } = getGetPageDeals();
  const { data: bannedTermsData, isLoading: isLoadingBannedTerms } =
    useGetBannedTerms();
  const {
    data: popularBrands,
    isLoading: isLoadingPopularBrands,
    refetch: refetchPopularBrands
  } = useGetPopularBrands();
  const { data: user, isLoading } = useGetUserData();
  const { data: dealLists, isLoading: isLoadingDealLists } =
    useGetDealListsEnabled();
  const { data: categories, isLoading: isLoadingCategories } =
    useGetCategories();
  const bannedTerms = bannedTermsData || [];
  if (
    !user?.isAdmin ||
    isLoading ||
    isLoadingPageDeals ||
    isLoadingDealLists ||
    isLoadingPopularBrands ||
    isLoadingCategories ||
    isLoadingBannedTerms
  ) {
    return (
      <>
        <Loading minHeight="initial" margin="12px auto 26px auto" />
        <Divider />
      </>
    );
  }

  const bannedTermsForDealLists = bannedTerms
    .filter((term) => term.type === 'NOT_POSTING_DEAL_LISTS')
    .map((term) => term.term);

  const filterCategories = (categories: CategoryType[]) =>
    filter(
      categories,
      (cat) =>
        cat.numLiveDeals > 6 &&
        !bannedCategories.includes(cat.category) &&
        !some(pageDeals, { category: cat.category })
    );

  const filterLists = (lists: DealList[]) => {
    // Filter the lists based on the conditions
    const filteredLists = filter(
      lists || [],
      (list) =>
        list.numLiveDeals > 6 &&
        !some(pageDeals, { dealListId: list.id }) &&
        !bannedTermsForDealLists.includes(list.name)
    );

    // Sort the filtered lists by the number of live deals (descending)
    const sortedLists = sortBy(filteredLists, (list) => -list.numLiveDeals);

    return sortedLists;
  };

  const filterBrands = (brands: PopularBrand[]) =>
    filter(
      brands,
      (brand) =>
        // !bannedBrands.includes(brand.brand) &&
        !some(pageDeals, { brand: brand.brand, type: 'BRAND' })
    );

  const renderMenuItems = <T extends MenuItemType>(
    items: T[],
    type: string,
    titlePrefix: string,
    // typeof chip color
    color: 'primary' | 'success' | 'info' | 'warning' | 'secondary',
    keyName: keyof T,
    letter: string,
    onDelete?: (item: T) => void
  ): JSX.Element[] =>
    items.map((item) => (
      <Chip
        variant="outlined"
        key={String(item[keyName])}
        sx={{
          marginBottom: '8px',
          marginRight: '8px',
          color:
            isDarkMode && color === 'primary'
              ? theme.palette.linkColor
              : undefined,
          borderColor:
            isDarkMode && color === 'primary'
              ? theme.palette.linkColor
              : undefined
        }}
        onDelete={onDelete ? () => onDelete(item) : undefined}
        color={color}
        avatar={<Avatar>{letter}</Avatar>}
        // value={String(item[keyName])}
        onClick={() => {
          const extra: {
            dealListId?: number;
          } = {};

          if (type === 'DEAL_LIST') {
            extra.dealListId = item.id;
          }

          handleOpenModal(null, {
            type,
            title: `${titlePrefix} ${item[keyName]}`,
            menuTitle: `Deals on ${item[keyName]}`,
            unsanitizedSlug: `Amazon Deals ${item[keyName]}`,
            [keyName]: item[keyName],
            ...extra
          });
        }}
        label={`${item[keyName]} (${item.numLiveDeals || item.count})`}
      />
    ));

  const dealListsRendered = filterLists(dealLists || []);
  const popularBrandsRendered = filterBrands(popularBrands || []);
  const categoriesRendered = filterCategories(categories || []);

  if (
    !dealListsRendered.length &&
    !popularBrandsRendered.length &&
    !categoriesRendered.length
  ) {
    return null;
  }

  return (
    <Box marginBottom="16px">
      <Box marginBottom="8px">
        <Typography variant="overline">Page deal suggestions</Typography>
      </Box>
      {pageDeals && (
        <Box>
          {renderMenuItems(
            dealListsRendered,
            'DEAL_LIST',
            'Amazon Deals on',
            'primary',
            'name',
            'DL',
            (item) => {
              Swal.fire({
                title: 'Are you sure?',
                text: `Are you sure you want to delete this deal list "${item.name}" from being considered from being posted?`,
                showCancelButton: true,
                confirmButtonText: 'Remove',
                denyButtonText: `Don't remove`
              }).then((result) => {
                if (result.isConfirmed) {
                  addBannedTerm({
                    term: item.name,
                    type: 'NOT_POSTING_DEAL_LISTS',
                    exact: true
                  });
                }
              });
            }
          )}
          {renderMenuItems(
            popularBrandsRendered,
            'BRAND',
            "Amazon's BEST Deals on",
            'success',
            'brand',
            'B',
            (item) => {
              Swal.fire({
                title: 'Are you sure?',
                text: `Are you sure you want to delete this brand "${item.brand}" from being considered from being posted?`,
                showCancelButton: true,
                confirmButtonText: 'Remove',
                denyButtonText: `Don't remove`
              }).then((result) => {
                if (result.isConfirmed) {
                  addBannedTerm(
                    {
                      term: item.brand,
                      type: 'NOT_POSTING_BRAND',
                      exact: true
                    },
                    {
                      onSuccess: () => {
                        // refetch popular brands
                        refetchPopularBrands();
                      }
                    }
                  );
                }
              });
            }
          )}
          {renderMenuItems(
            categoriesRendered,
            'CATEGORY',
            'Amazon Deals on Select',
            'warning',
            'category',
            'C'
          )}
        </Box>
      )}
    </Box>
  );
};

export default AvailableNewPageDeals;
